import { useRef, useState } from 'react'
import { get } from '../../../../../utils/Api'
import { PID_MAX_LENGTH } from '../../../../../utils/productIdentifiers'
import { useBooking } from './ctx'
import { CURRENCIES, CUR_SYM } from './constants'
import { Combo, ErrorText, Field, Ic, Note, NumStepper, SelectField, Sheet } from './fields'
import type { ComboOption } from './fields'
import { actualKg, blankItem, itemsKg, itemsValue, naira, needsPids, num } from './rules'
import type { Code, Item } from './types'

const money = (n: number, cur: string) =>
  `${CUR_SYM[cur] || ''}${Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`

/** Customs code search, same endpoint the old Specify Items step used. */
function useHsLookup() {
  const { f, set } = useBooking()
  const seq = useRef<Record<number, number>>({})
  return (i: number, it: Pick<Item, 'desc' | 'material' | 'usage'>) => {
    const desc = it.desc.trim()
    if (desc.length < 3) return
    const mine = (seq.current[i] = (seq.current[i] || 0) + 1)
    set((d) => void (d.items[i].hs = 'loading'))
    const qs = new URLSearchParams({
      countryCode: f.to,
      itemDescription: desc,
      itemMadeOf: it.material.trim(),
      itemUsage: it.usage.trim(),
    })
    get(`booking/fetch-hs-code?${qs.toString()}`)
      .then((result: any) => {
        if (seq.current[i] !== mine) return
        const opts: Code[] =
          result.status === 200 && Array.isArray(result.data)
            ? result.data.map((x: any) => ({ value: String(x.code), label: String(x.description || '') }))
            : []
        set((d) => {
          const item = d.items[i]
          if (!item) return
          item.opts = opts
          item.hs = opts.length ? 'done' : 'none'
          // Keep a code the agent already chose if it's still offered
          if (!item.code || !opts.some((o) => o.value === item.code?.value)) item.code = opts[0] || null
        })
      })
      .catch(() => {
        if (seq.current[i] === mine) set((d) => d.items[i] && void (d.items[i].hs = 'none'))
      })
  }
}

function HsRow({ i, onOpen }: { i: number; onOpen: () => void }) {
  const { f, err } = useBooking()
  const it = f.items[i]
  const eid = `it${i}-code`
  let body
  let btn = ''
  if (it.hs === 'loading')
    body = (
      <span className='cs-muted' role='status'>
        <Ic name='loader-circle' className='spin' /> Finding the code…
      </span>
    )
  else if (it.code) {
    body = (
      <span className='cs-val' title={`${it.code.value} ${it.code.label}`}>
        <code>{it.code.value}</code>
        {it.code.label}
      </span>
    )
    btn = 'Change'
  } else if (it.hs === 'none') {
    body = (
      <span className='cs-warn'>
        <Ic name='circle-alert' /> No match for “{it.desc}”
      </span>
    )
    btn = 'Find code'
  } else if (it.desc.trim().length >= 3) {
    body = <span className='cs-muted'>Not looked up yet</span>
    btn = 'Find code'
  } else body = <span className='cs-muted'>Suggested once you describe the item</span>
  return (
    <div className='bk-field'>
      <div className='cs-row'>
        <div className='cs-main'>
          <span className='cs-lbl'>Customs code</span>
          {body}
        </div>
        {btn && (
          <button
            type='button'
            className='bk-btn quiet sm'
            id={eid}
            aria-haspopup='dialog'
            aria-invalid={err(eid) ? true : undefined}
            aria-describedby={err(eid) ? `${eid}-err` : undefined}
            onClick={onOpen}
          >
            {btn}
            <span className='bk-sr'> customs code for item {i + 1}</span>
          </button>
        )}
      </div>
      <ErrorText id={eid} msg={err(eid)} />
    </div>
  )
}

function PidRow({ i, onOpen }: { i: number; onOpen: () => void }) {
  const { f, err } = useBooking()
  const p = f.items[i].pid
  const eid = `it${i}-pid`
  const have = [p.merchant, p.manufacturer].filter((x) => x.trim()).length
  return (
    <div className='bk-field'>
      <div className='cs-row'>
        <div className='cs-main'>
          <span className='cs-lbl'>EU product codes</span>
          {have === 2 ? (
            <span className='cs-val'>{[p.merchant, p.manufacturer, p.standardisedManufacturer].filter(Boolean).join(' · ')}</span>
          ) : (
            <span className={have ? 'cs-warn' : 'cs-muted'}>{have} of 2 required codes added</span>
          )}
        </div>
        <button
          type='button'
          className='bk-btn quiet sm'
          id={eid}
          aria-haspopup='dialog'
          aria-invalid={err(eid) ? true : undefined}
          aria-describedby={err(eid) ? `${eid}-err` : undefined}
          onClick={onOpen}
        >
          {have === 2 ? 'Edit' : 'Add codes'}
          <span className='bk-sr'> EU product codes for item {i + 1}</span>
        </button>
      </div>
      <ErrorText id={eid} msg={err(eid)} />
    </div>
  )
}

function DescCombo({ i, onLookup }: { i: number; onLookup: (it: Item) => void }) {
  const { f, set, err, clearErr, customer } = useBooking()
  const it = f.items[i]
  const [opts, setOpts] = useState<ComboOption<any>[]>([])
  const timer = useRef<any>(null)
  const looked = useRef(it.desc.trim())

  // The customer's previously shipped items (with their codes), as the old form offered
  const suggest = (text: string) => {
    clearTimeout(timer.current)
    if (!customer || text.trim().length < 2) return setOpts([])
    timer.current = setTimeout(() => {
      get(`booking/item-suggestions?q=${encodeURIComponent(text.trim())}&userId=${customer.value}`)
        .then((result: any) =>
          setOpts(
            result.status === 200 && Array.isArray(result.data)
              ? result.data.slice(0, 6).map((s: any, n: number) => ({
                  key: `${n}-${s.description}`,
                  title: s.description,
                  sub: [s.commodityCodes?.value && `HS ${s.commodityCodes.value}`, 'shipped before'].filter(Boolean).join(' · '),
                  value: s,
                }))
              : [],
          ),
        )
        .catch(() => setOpts([]))
    }, 300)
  }

  return (
    <Combo
      id={`it${i}-desc`}
      label='What is it?'
      hint='Be specific — “men’s cotton shirts”, not “clothes”'
      value={it.desc}
      options={opts}
      listHead='Shipped before'
      placeholder='Start typing…'
      error={err(`it${i}-desc`)}
      onInput={(v) => {
        set((d) => void (d.items[i].desc = v))
        clearErr(`it${i}-desc`)
        suggest(v)
      }}
      onBlur={() => {
        // Look the code up once the description settles, not on every keystroke
        const now = f.items[i].desc.trim()
        if (now.length >= 3 && now !== looked.current) {
          looked.current = now
          onLookup(f.items[i])
        }
      }}
      onPick={(o) => {
        const s = o.value
        looked.current = String(s.description || '').trim()
        set((d) => {
          const item = d.items[i]
          item.desc = s.description || ''
          if (s.commodityCodes?.value) {
            item.code = { value: String(s.commodityCodes.value), label: String(s.commodityCodes.label || '') }
            item.opts = [item.code]
            item.hs = 'done'
          }
          if (s.productIdentifiers) {
            item.pid = {
              merchant: s.productIdentifiers.merchant || '',
              manufacturer: s.productIdentifiers.manufacturer || '',
              standardisedManufacturer: s.productIdentifiers.standardisedManufacturer || '',
            }
          }
        })
        clearErr(`it${i}-desc`, `it${i}-code`, `it${i}-pid`)
        setOpts([])
        setTimeout(() => document.getElementById(`it${i}-price`)?.focus(), 0)
      }}
    />
  )
}

function Meters() {
  const { f, err } = useBooking()
  const parcel = actualKg(f)
  const kg = itemsKg(f)
  const overW = kg > parcel + 1e-9
  const cur = f.currency
  const val = itemsValue(f)
  const cap = num(f.value)
  const overV = cur === 'NGN' && val > cap + 1e-9
  const pct = (a: number, b: number) => (b ? Math.min(100, (a / b) * 100) : 0)
  return (
    <div className='bk-meters'>
      <div className={`bk-meter ${overW ? 'over' : ''}`} id='meter-w'>
        <div className='mt'>
          <span>Items weight</span>
          <span>of {parcel.toFixed(1)} kg parcel</span>
        </div>
        <div className='mv'>{kg.toFixed(1)} kg</div>
        <div className='bar' role='img' aria-label={`${kg.toFixed(1)} of ${parcel.toFixed(1)} kilograms`}>
          <i style={{ width: `${pct(kg, parcel)}%` }} />
        </div>
        {overW ? (
          <p className='ms bad'>
            <Ic name='circle-alert' />
            <span>{(kg - parcel).toFixed(1)} kg more than the parcel. Lower the item weights or update the parcel weight in step 1.</span>
          </p>
        ) : kg ? (
          <p className='ms ok'>
            <Ic name='circle-check' />
            <span>Fits the parcel weight</span>
          </p>
        ) : (
          <p className='ms neutral'>
            <Ic name='info' />
            <span>Add each item’s weight</span>
          </p>
        )}
        {err('meter-w') && !overW && <ErrorText id='meter-w' msg={err('meter-w')} />}
      </div>
      {cur !== 'NGN' ? (
        <div className='bk-meter' id='meter-v'>
          <div className='mt'>
            <span>Declared value for customs</span>
          </div>
          <div className='mv'>{money(val, cur)}</div>
          <p className='ms neutral'>
            <Ic name='info' />
            <span>The item total in {cur}. It’s printed on the customs invoice.</span>
          </p>
        </div>
      ) : (
        <div className={`bk-meter ${overV ? 'over' : ''}`} id='meter-v'>
          <div className='mt'>
            <span>Items value</span>
            <span>of {naira(cap)} declared</span>
          </div>
          <div className='mv'>{naira(val)}</div>
          <div className='bar' role='img' aria-label={`${naira(val)} of ${naira(cap)}`}>
            <i style={{ width: `${pct(val, cap)}%` }} />
          </div>
          {overV ? (
            <p className='ms bad'>
              <Ic name='circle-alert' />
              <span>{naira(val - cap)} more than the value in step 1. Lower the item prices or update “What is it worth?”.</span>
            </p>
          ) : val ? (
            <p className='ms ok'>
              <Ic name='circle-check' />
              <span>Within the declared value</span>
            </p>
          ) : (
            <p className='ms neutral'>
              <Ic name='info' />
              <span>Add each item’s price</span>
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default function ItemsStep() {
  const { f, set, err, clearErr } = useBooking()
  const lookup = useHsLookup()
  const [sheet, setSheet] = useState<{ kind: 'hs' | 'pid'; i: number } | null>(null)
  const pids = needsPids(f)
  const cur = f.currency
  const sIt = sheet ? f.items[sheet.i] : null

  const setItem = (i: number, key: 'qty' | 'price' | 'weight', v: string) => {
    set((d) => void (d.items[i][key] = v))
    clearErr(`it${i}-${key}`, key === 'weight' ? 'meter-w' : key === 'price' ? 'meter-v' : '')
  }

  return (
    <fieldset className='bk-grp'>
      <legend>What’s in the parcel?</legend>
      <p className='bk-intro'>List each kind of item. Customs uses this to clear the parcel.</p>
      <SelectField
        id='currency'
        label='Currency for item prices'
        hint='Enter every price below in this currency. Nothing is converted.'
        value={cur}
        options={CURRENCIES}
        onChange={(v) => {
          set((d) => void (d.currency = v || 'NGN'))
          clearErr('meter-v')
        }}
      />

      {f.items.map((it, i) => (
        <fieldset className='bk-item' key={i}>
          <legend>Item {i + 1}</legend>
          {f.items.length > 1 && (
            <div className='bk-item-tools'>
              <button type='button' className='bk-btn danger-quiet sm' onClick={() => set((d) => void d.items.splice(i, 1))}>
                <Ic name='trash-2' /> Remove<span className='bk-sr'> item {i + 1}</span>
              </button>
            </div>
          )}
          <DescCombo i={i} onLookup={(item) => lookup(i, item)} />
          <div className='bk-row item3'>
            <div className='bk-field'>
              <label htmlFor={`it${i}-qty`} id={`it${i}-qty-l`}>
                Quantity
              </label>
              <NumStepper
                id={`it${i}-qty`}
                labelledBy={`it${i}-qty-l`}
                label='quantity'
                min={1}
                value={it.qty}
                error={err(`it${i}-qty`)}
                onChange={(v) => setItem(i, 'qty', v)}
              />
              <ErrorText id={`it${i}-qty`} msg={err(`it${i}-qty`)} />
            </div>
            <Field
              id={`it${i}-price`}
              label='Price each'
              type='number'
              inputMode='decimal'
              min='0'
              step='0.01'
              pre={CUR_SYM[cur]}
              value={it.price}
              error={err(`it${i}-price`)}
              onChange={(v) => setItem(i, 'price', v)}
            />
            <Field
              id={`it${i}-weight`}
              label='Total weight'
              type='number'
              inputMode='decimal'
              min='0'
              step='0.1'
              suf='kg'
              value={it.weight}
              error={err(`it${i}-weight`)}
              onChange={(v) => setItem(i, 'weight', v)}
            />
          </div>
          <div className='cs-rows'>
            <HsRow i={i} onOpen={() => setSheet({ kind: 'hs', i })} />
            {pids && <PidRow i={i} onOpen={() => setSheet({ kind: 'pid', i })} />}
          </div>
        </fieldset>
      ))}

      <button type='button' className='bk-btn secondary sm' onClick={() => set((d) => void d.items.push(blankItem()))}>
        <Ic name='plus' /> Add another item
      </button>
      <Meters />

      <Sheet
        open={!!sheet}
        title={sheet ? (sheet.kind === 'hs' ? `Customs code — item ${sheet.i + 1}` : `EU product codes — item ${sheet.i + 1}`) : ''}
        onClose={() => {
          if (sheet && sIt) {
            if (sIt.code) clearErr(`it${sheet.i}-code`)
            if (sIt.pid.merchant.trim() && sIt.pid.manufacturer.trim()) clearErr(`it${sheet.i}-pid`)
          }
          setSheet(null)
        }}
      >
        {sheet && sIt && sheet.kind === 'hs' && (
          <>
            <p className='bk-hint mb'>For “{sIt.desc}”. Customs uses this code to classify the item.</p>
            {sIt.hs === 'loading' ? (
              <div role='status' className='bk-skel-wrap mb'>
                <span className='bk-sr'>Finding the customs code…</span>
                <div className='bk-skel' style={{ width: '70%' }} />
                <div className='bk-skel' style={{ width: '45%' }} />
              </div>
            ) : sIt.opts.length ? (
              <div className='bk-hs-list mb' role='radiogroup' aria-label='Customs code options'>
                {sIt.opts.map((o, n) => (
                  <label className='bk-hs-opt' key={`${o.value}-${n}`}>
                    <input
                      type='radio'
                      name={`it${sheet.i}-code-sheet`}
                      checked={sIt.code?.value === o.value}
                      onChange={() => {
                        set((d) => void (d.items[sheet.i].code = o))
                        clearErr(`it${sheet.i}-code`)
                      }}
                    />
                    <span>
                      <code>{o.value}</code>
                      <span>{o.label}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <Note tone='warn' icon='circle-alert'>
                <p>No match yet. Say what it’s made of and what it’s used for, then search again.</p>
              </Note>
            )}
            <p className='bk-sheet-sub'>{sIt.opts.length ? 'Not right? Refine the search' : 'Help us find it'}</p>
            <div className='bk-row c2'>
              <Field
                id={`it${sheet.i}-mat`}
                label='What is it made of?'
                optional
                value={sIt.material}
                onChange={(v) => set((d) => void (d.items[sheet.i].material = v))}
              />
              <Field
                id={`it${sheet.i}-use`}
                label='What is it used for?'
                optional
                value={sIt.usage}
                onChange={(v) => set((d) => void (d.items[sheet.i].usage = v))}
              />
            </div>
            <button type='button' className='bk-btn secondary sm' onClick={() => lookup(sheet.i, sIt)} disabled={sIt.desc.trim().length < 3}>
              <Ic name='search' /> Search again
            </button>
          </>
        )}
        {sheet && sIt && sheet.kind === 'pid' && (
          <>
            <p className='bk-hint mb'>EU customs needs these for every item. The seller or supplier has them.</p>
            {(
              [
                ['merchant', 'Seller’s product code (SKU)', false],
                ['manufacturer', 'Manufacturer’s part or model number', false],
                ['standardisedManufacturer', 'Barcode (GTIN / EAN / UPC)', true],
              ] as const
            ).map(([key, label, optional]) => (
              <Field
                key={key}
                id={`it${sheet.i}-pid-${key}`}
                label={label}
                optional={optional}
                maxLength={PID_MAX_LENGTH}
                inputMode={key === 'standardisedManufacturer' ? 'numeric' : undefined}
                value={sIt.pid[key]}
                onChange={(v) => set((d) => void (d.items[sheet.i].pid[key] = v))}
              />
            ))}
          </>
        )}
      </Sheet>
    </fieldset>
  )
}
